import { Ban, Clock } from 'lucide-react';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface OrganizationBlockedProps {
  title: string;
  message: string;
  hint?: string;
  icon?: ReactNode;
}

/**
 * Komunikat pokazywany ZAMIAST treści widoku, gdy panel jest zablokowany
 * (wyłączona organizacja, wygasły plan). Nawigacja zostaje na miejscu —
 * użytkownik ma się dowiedzieć, co się stało, a nie utknąć na pustym ekranie
 * albo zostać wyrzucony z aplikacji.
 */
export const OrganizationBlocked: FC<OrganizationBlockedProps> = ({
  title,
  message,
  hint,
  icon,
}) => (
  <div className="w-full flex items-center justify-center min-h-[60vh] px-4">
    <div className="text-center max-w-md w-full bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
      <div className="flex justify-center mb-4 text-gray-400" aria-hidden>
        {icon ?? <Ban size={56} strokeWidth={1.5} />}
      </div>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>
      <p className="text-gray-600 mb-2">{message}</p>
      {hint && <p className="text-sm text-gray-500">{hint}</p>}
    </div>
  </div>
);

/** Wariant dla wygasłego okresu próbnego (dawniej: ekran wyboru planu). */
export const TrialExpired: FC = () => {
  const { t } = useTranslation();

  return (
    <OrganizationBlocked
      title={t('trialExpired.title', 'Okres próbny zakończony')}
      message={t(
        'trialExpired.message',
        'Twój okres próbny dobiegł końca i panel jest obecnie niedostępny.',
      )}
      hint={t(
        'trialExpired.contact',
        'Skontaktuj się z opiekunem konta HotelSpot, aby przedłużyć dostęp.',
      )}
      icon={<Clock size={56} strokeWidth={1.5} />}
    />
  );
};

/** Wariant dla `organizations/{id}.isDisabled === true`. */
export const OrganizationDisabled: FC = () => {
  const { t } = useTranslation();

  return (
    <OrganizationBlocked
      title={t('organizationDisabled.title', 'Organizacja wyłączona')}
      message={t(
        'organizationDisabled.message',
        'Ta organizacja została wyłączona i panel jest obecnie niedostępny.',
      )}
      hint={t(
        'organizationDisabled.contact',
        'Skontaktuj się z opiekunem konta HotelSpot, jeśli potrzebujesz pomocy.',
      )}
    />
  );
};
